import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { AppState } from '../../interfaces';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Router } from '@angular/router';
import * as userSelector from '../../core/store/order/order.selectors';
import * as uiSelector from '../../core/store/ui/ui.selectors';
import { Observable, Subject } from 'rxjs';
import { AuthService } from '../../core/services/auth.service';
import { DataService } from 'src/app/core/services/data.service';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { LoggerService } from 'src/app/core/services/logger.service';
import { FormGroup, FormBuilder, Validators, FormControl } from '@angular/forms';
import { debounceTime, delay } from 'rxjs/operators';
import { projectID } from 'src/app/core/data/theme-config';
import { UserType } from 'src/app/core/models/user';
import { SetLoginCode } from 'src/app/core/store/ui';
import { environment } from 'src/environments/environment';
import { joinPaths } from 'src/app/core/utils/paths/join';
import { GtmService } from 'src/app/core/services/google-tag-manager.service';
import { ActivatedRoute } from '@angular/router';

import { AddressDTO } from 'src/app/core/models/OrderInfoDTO';

type RecoverPasswordWindowType = 'passwordLost' | 'newUser';

@Component({
  selector: 'app-login-dialog',
  templateUrl: './login-dialog.component.html',
  styleUrls: ['./login-dialog.component.scss']
})
export class LoginDialogComponent implements OnInit, OnDestroy {
  get isHainautOrNamurorNVN() {
    return projectID === 'namur' || projectID === 'hainaut' || projectID === 'nvn' || projectID === 'liege';
  }

  $isLoggedIn: Observable<boolean>;
  $isTablette: Observable<boolean>;
  $loginCode: Observable<number>;

  debouncer: any;
  emailSent = false;
  emailAsked = false;
  emailError = false;
  emailNotInDB = false;
  language = 'fr';

  emailFoundForOrderUserId = '';
  userIdFoundForOrderUserId = '';

  displayForgotInfoWindow = false;
  recoverPasswordWindowType: RecoverPasswordWindowType = 'passwordLost';
  displayEmailProposition = false;
  showOthers = false;

  userFG: FormGroup;

  loginStatus: Subject<number>;
  isLogging: boolean = false;

  UserTypes = UserType;
  userTypeSelected = UserType.Notaire;
  isPasswordVisible = false;
  loginRedirectUrl: string | undefined;
  isSubmittingForgottenPassword = false;

  others = [
    UserType.Administration_Communale,
    UserType.Architecte,
    UserType.Avocat,
    UserType.Certifier,
    UserType.Expert_Immobilier,
    UserType.Surveyor,
    UserType.Promoteur,
    UserType.Other
  ];

  userInfo = {
    lastName: '',
    firstName: '',
    phone: '',
    email: '',
    company: '',
    tva: '',
    street: '',
    num: '',
    zip: '',
    city: '',
    userLocation: ''
  };

  constructor(
    private authService: AuthService,
    public dataService: DataService,
    public breakpointObserver: BreakpointObserver,
    private store: Store<AppState>,
    private http: HttpClient,
    private snackBar: MatSnackBar,
    private logger: LoggerService,
    private _formBuilder: FormBuilder,
    private gtmService: GtmService,
    private route: ActivatedRoute,
    private router: Router,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      register: boolean;
      forgotten: boolean;
      recoverPasswordWindowType: RecoverPasswordWindowType;
      selectedUserType: number;
      dialog: MatDialog;
    }
  ) {
    this.userTypeSelected = this.data.selectedUserType;
    this.displayForgotInfoWindow = this.data.forgotten;
    this.recoverPasswordWindowType = this.data.recoverPasswordWindowType || 'passwordLost';
    this.userFG = this._formBuilder.group({
      firstName: [{ value: this.userInfo.firstName, disabled: false }, Validators.required],
      lastName: [{ value: this.userInfo.lastName, disabled: false }, Validators.required],
      phone: [{ value: this.userInfo.phone, disabled: false }, Validators.required],
      email: [
        { value: this.userInfo.email, disabled: false },
        [Validators.required, Validators.email],
        this.isEmailUnique.bind(this)
      ],
      company: [{ value: this.userInfo.company, disabled: false }, Validators.required],
      tva: [{ value: this.userInfo.tva, disabled: false }, Validators.required],
      userLocation: [{ value: this.userInfo.userLocation, disabled: false }]
    });
  }

  ngOnInit() {
    this.$isLoggedIn = this.store.select(userSelector.getIsLoggedIn);
    this.$isTablette = this.store.select(uiSelector.getIsTablette);
    this.$loginCode = this.store.select(uiSelector.getLoginCode);
    this.$isLoggedIn.pipe(delay(100)).subscribe(logged => {
      if (logged) {
        this.closeDialog();
      }
    });
    this.loginStatus = this.authService.loginStatus;

    this.store.select(uiSelector.getLanguage).subscribe(ln => (this.language = ln));

    this.userFG.valueChanges.pipe(debounceTime(1000)).subscribe(values => {
      this.userInfo.firstName = values.firstName;
      this.userInfo.lastName = values.lastName;
      this.userInfo.phone = values.phone;
      this.userInfo.email = values.email;
      this.userInfo.company = values.company;
      this.userInfo.tva = values.tva;
      this.userInfo.street = values.street;
      this.userInfo.num = values.num;
      this.userInfo.city = values.city;
      this.userInfo.zip = values.zip;
      this.userInfo.userLocation = values.userLocation;
    });

    const selectedUserTypeString = this.data.selectedUserType;
    this.route.queryParamMap.subscribe(params => {
      this.loginRedirectUrl = params.get('login-redirect-url') || undefined;
    });
    this.store.dispatch(new SetLoginCode(200));
  }

  ngOnDestroy() {
    this.store.dispatch(new SetLoginCode(200));
  }

  signIn(email, pw) {
    if (this.isLogging) return;
    if (email !== '' && pw !== '') {
      this.isLogging = true;
      this.authService.login(email, pw, this.loginRedirectUrl).subscribe({
        next: () => {
          this.isLogging = false;
        },
        error: () => {
          this.isLogging = false;
        }
      });
    }
  }

  handleSelectedAddressChanged(addr: AddressDTO & { PostalCode: string }) {
    console.log('handleSelectedAddressChanged', addr);
    // set general form data:
    this.userInfo.street = String(addr.Street);
    this.userInfo.num = String(addr.Number);
    this.userInfo.zip = String(addr.PostalCode || addr.Zip);
    this.userInfo.city = String(addr.City);
    // set userLocation:
    const buildString = this.dataService
      ? this.dataService.formatAddress({
          ...addr,
          Zip: Number(addr.PostalCode || addr.Zip || 0)
        } as AddressDTO)
      : null;
    this.userInfo.userLocation = String(buildString);
  }

  submitForgotPassword(email: string) {
    if (this.isSubmittingForgottenPassword) return;
    this.isSubmittingForgottenPassword = true;
    this.authService.forgotPassword(email).subscribe({
      next: () => {
        this.isSubmittingForgottenPassword = false;
        this.emailSent = true;
        this.gtmService.emit('account_recovery_fired', {
          has_clicked_on_password_lost: this.recoverPasswordWindowType === 'passwordLost'
        });
      },
      error: response => {
        this.isSubmittingForgottenPassword = false;
        this.logger.log('POST call in error', response);
        this.snackBar.open('Une erreur a eu lieu 😰');
        this.emailError = true;
      }
    });
  }

  closeDialog() {
    this.data.dialog.closeAll();
  }

  selectUserType(type: UserType) {
    if (type === UserType.Other) {
      this.showOthers = true;
    } else if (type === UserType.Agence || type === UserType.Notaire || type === UserType.Installateur) {
      this.showOthers = false;
    }
    this.logger.log(type);
    this.userTypeSelected = type;
  }

  loginErrorMessageTranslationKey(code?: number): string {
    return (
      {
        401: 'login-modal.connection.password-error',
        429: 'login-modal.connection.ratelimit-error'
      }[code] || 'login-modal.connection.password-error'
    );
  }

  selectedClass(selectedId) {
    const cls = this.userTypeSelected === selectedId ? 'selected' : '';
    return cls;
  }

  askForProAccount() {
    console.log('askForProAccount', this.userFG, this.userInfo.userLocation);
    if (this.userFG.invalid || !this.userInfo.userLocation.length) {
      return;
    }
    if (isNaN(Number(this.userInfo.zip))) return;
    const userInfo = {
      ConsumerType: 1688,
      Users: {
        Name: this.userInfo.lastName,
        Email: this.userInfo.email,
        FirstName: this.userInfo.firstName,
        PhoneNumber: this.userInfo.phone,
        Language:
          this.language === 'nl'
            ? 'nl-BE'
            : this.language === 'en'
              ? 'en-BE'
              : this.language === 'de'
                ? 'de-BE'
                : 'fr-BE',
        Addresses: {
          Company: this.userInfo.company,
          VatNumber: this.userInfo.tva,
          Street: this.userInfo.street,
          Number: this.userInfo.num,
          PostalCode: Number(this.userInfo.zip),
          City: this.userInfo.city
        }
      },
      Comment: projectID + ' ' + this.userTypeSelected
    };

    this.http.post<any>(joinPaths(environment.CertiSystems.createNewUser, 'SaveOrderClient'), userInfo).subscribe(
      val => {
        this.logger.log(`🚀`);
        this.logger.log('POST call successful value returned in body', val);
        this.snackBar.open('🚀 Email envoyé avec succès 🚀');
        this.emailSent = true;
        // this.authService.loginWithUserIDAsync(val.UserID);
        this.closeDialog();
      },
      response => {
        this.logger.log('POST call in error', response);
        this.snackBar.open('Une erreur a eu lieu 😰');
        this.emailError = true;
      }
    );
  }

  isEmailUnique(control: FormControl) {
    clearTimeout(this.debouncer);

    return new Promise((resolve, reject) => {
      this.debouncer = setTimeout(() => {
        this.authService.isEmailAlreadyInUse(control.value).subscribe(data => {
          if (data.Data) {
            this.logger.log('Email exists', data.Data);
            resolve({ isEmailAlreadyInUse: true });
          } else {
            this.logger.log(`🚀`);
            resolve(null);
          }
        });
      }, 1000);
    });
  }

  showForgotInfo(recoverPasswordWindowType: RecoverPasswordWindowType) {
    this.displayForgotInfoWindow = true;
    this.recoverPasswordWindowType = recoverPasswordWindowType;
  }

  expandRegister() {
    this.data.register = true;
  }

  expandConnect() {
    this.data.register = false;
  }

  close() {
    this.data.dialog.closeAll();
  }

  togglePasswordVisibility() {
    this.isPasswordVisible = !this.isPasswordVisible;
  }
}
